import { captureException } from '@sentry/react';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { SongPreview } from '~/interfaces';
import useSongIndex from '~/modules/Songs/hooks/useSongIndex';
import { getKaraokeMap, type KaraokeMeta } from '~/modules/Karaoke/karaokeCatalog';
import useRecommendedSongs from '~/routes/SingASong/SongSelection/Hooks/useRecommendedSongs';
import { useSongListFilter } from '~/routes/SingASong/SongSelection/Hooks/useSongListFilter';

export interface SongGroup {
  name: string;
  displayShort?: ReactNode;
  displayLong?: ReactNode;
  songs: Array<{ index: number; song: SongPreview; isPopular: boolean }>;
  isNew?: boolean;
}

const groupSongsByLetter = (song: SongPreview): Pick<SongGroup, 'name'> => {
  const nonAlphaRegex = /[^a-zA-Z]/;

  return {
    name: isFinite(+song.artist[0]) || nonAlphaRegex.test(song.artist[0]) ? '0-9' : song.artist[0].toUpperCase(),
  };
};

async function fetchKaraokeIndex(): Promise<SongPreview[]> {
  const res = await fetch('/karaoke/index.json', { cache: 'no-store' });
  if (!res.ok) return [];
  const data = (await res.json()) as SongPreview[];
  return Array.isArray(data) ? data.filter((song) => !song.hiddenFromCatalog) : [];
}

function mergeSongWithKaraokeMeta(song: SongPreview, meta?: KaraokeMeta): SongPreview {
  if (!meta) return song;

  return {
    ...song,
    provider: song.provider ?? meta.provider,
    video: song.video ?? meta.video,
    url: song.url ?? meta.url,
    uid: song.uid ?? meta.uid,
  };
}

export default function useSongList(additionalSong: string | null) {
  const songIndex = useSongIndex();

  const [karaokeMap, setKaraokeMap] = useState<Map<string, KaraokeMeta> | null>(null);
  const [karaokeSongs, setKaraokeSongs] = useState<SongPreview[] | null>(null);
  const [karaokeLoading, setKaraokeLoading] = useState(true);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const map = await getKaraokeMap();
        const songs = await fetchKaraokeIndex();

        if (!alive) return;
        setKaraokeMap(map);
        setKaraokeSongs(songs);
      } catch {
        if (!alive) return;
        setKaraokeMap(new Map());
        setKaraokeSongs([]);
      } finally {
        if (!alive) return;
        setKaraokeLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // Si hay catálogo karaoke cargado y tiene canciones, úsalo como base.
  // Si no, usa el index normal.
  const baseSongListRaw = karaokeSongs && karaokeSongs.length > 0 ? karaokeSongs : songIndex.data;

  // Normaliza provider/video/url/uid para que toda la app los tenga disponibles.
  const baseSongList = useMemo(() => {
    if (!karaokeMap) return baseSongListRaw;

    return (baseSongListRaw ?? []).map((song) => mergeSongWithKaraokeMeta(song, karaokeMap.get(song.id)));
  }, [baseSongListRaw, karaokeMap]);

  const {
    value: { popular },
    loading: recommendedLoading,
  } = useRecommendedSongs(baseSongList);

  const isLoading = songIndex.isLoading || recommendedLoading || karaokeLoading;

  const { filters, filteredList, setFilters, selectedPlaylist, setSelectedPlaylist, playlists, playlist } =
    useSongListFilter(baseSongList, popular, isLoading, additionalSong);

  // Si estamos usando karaokeSongs como base, no filtres por karaokeMap.
  // Si estamos usando songIndex normal, filtra por canciones presentes en el mapa karaoke.
  const finalList = useMemo(() => {
    const usingKaraoke = karaokeSongs && karaokeSongs.length > 0;
    if (usingKaraoke) return filteredList;

    if (!karaokeMap) return filteredList;
    return filteredList.filter((song) => karaokeMap.has(song.id));
  }, [filteredList, karaokeMap, karaokeSongs]);

  const groupedSongList = useMemo(() => {
    const groups: SongGroup[] = [];
    const list = finalList;

    if (filters.search) {
      groups.push({
        name: 'Search results',
        songs: list.map((song, index) => ({ index, song, isPopular: popular.includes(song.id) })),
      });
      return groups;
    }

    if (list.length === 0) return [];

    const sortedList = playlist?.sortingFn
      ? [...list].sort(playlist.sortingFn)
      : [...list].sort((a, b) =>
        (a.artist || '').localeCompare(b.artist || '', undefined, { sensitivity: 'base' }) ||
        (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
      );

    sortedList.forEach((song) => {
      try {
        const { name, ...rest } = (playlist?.groupData ?? groupSongsByLetter)(song);
        let group = groups.find((existingGroup) => existingGroup.name === name);

        if (!group) {
          group = { name, songs: [], ...rest };
          groups.push(group);
        }

        group.songs.push({
          index: list.indexOf(song),
          song,
          isPopular: popular.includes(song.id),
        });
      } catch (e) {
        console.error(e);
        captureException(e);
      }
    });

    return groups;
  }, [finalList, filters.search, popular, playlist]);

  return {
    groupedSongList,
    songList: finalList,
    filters,
    setFilters,
    isLoading,
    selectedPlaylist,
    setSelectedPlaylist,
    playlists,
  };
}